import * as React from "react";
import {Box} from "@mui/material";
import {getI18n, useTranslation} from "react-i18next";
import {Constants} from "@/api/constants";
import {RequestFilter} from "@/api/data-contracts";
import {getCurrencySymbol} from "@/lib/utils";
import NumberInputField from "@/lib/components/inputfields/number-input-field";
import CurrencyInputField from "@/lib/components/inputfields/currency-input-field";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import {KILOMETRE_UNIT, MILLIMETRE_UNIT} from "@/constants";
import {OverviewTableI18N, RequestI18N} from "@/translations";
import {RequestOverviewFilters} from "@/common/overview-state/request-view-state";
import {ColumnFilterRow} from "@/common/overview-table/ColumnFilterRow";
import {useDateFieldChange, useNumberFieldChange} from "@/pages/request-overview-page/RequestColumnFilters";
import {REQUEST_OVERVIEW_COLUMN_IDS} from "@/common/overview-table/overview-table-column-ids";

interface RequestRangeColumnFilterProps {
    /** The applied filters (Sofortwirkung) — read-only here, every change goes back through `onChange`. */
    filters: RequestOverviewFilters;
    /** Wired to `setFilters` by the caller — never writes onto the applied state directly. */
    onChange: (partial: Partial<RequestOverviewFilters>) => void;
}

/**
 * The "Innenmaße" column's filter content: all six contract fields in one dropdown, three von/bis rows for
 * length, width and height, no functional loss versus the removed Sheet's own six fields (plan
 * "Spaltenkopf-Filterung und Sortieranzeige", Phase 3.2, Entscheidung 7). Field order is Länge, Breite,
 * Höhe, the table column's own order, deliberately not width/height/length (#1608).
 */
const RequestInternalDimensionsColumnFilter: React.FC<RequestRangeColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const suffix = MILLIMETRE_UNIT;

    const handleLengthMinChange = useNumberFieldChange("internalDimensionLengthMin", onChange);
    const handleLengthMaxChange = useNumberFieldChange("internalDimensionLengthMax", onChange);
    const handleWidthMinChange = useNumberFieldChange("internalDimensionWidthMin", onChange);
    const handleWidthMaxChange = useNumberFieldChange("internalDimensionWidthMax", onChange);
    const handleHeightMinChange = useNumberFieldChange("internalDimensionHeightMin", onChange);
    const handleHeightMaxChange = useNumberFieldChange("internalDimensionHeightMax", onChange);

    const lengthFromLabel = `${t(RequestI18N.details.field.length)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const lengthToLabel = `${t(RequestI18N.details.field.length)} (${t(OverviewTableI18N.field.rangeTo)})`;
    const widthFromLabel = `${t(RequestI18N.details.field.width)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const widthToLabel = `${t(RequestI18N.details.field.width)} (${t(OverviewTableI18N.field.rangeTo)})`;
    const heightFromLabel = `${t(RequestI18N.details.field.height)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const heightToLabel = `${t(RequestI18N.details.field.height)} (${t(OverviewTableI18N.field.rangeTo)})`;

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
            <ColumnFilterRow fields={[{label: lengthFromLabel, suffix}, {label: lengthToLabel, suffix}]}>
                <NumberInputField
                    name="internalDimensionLengthMin"
                    label={lengthFromLabel}
                    value={filters.internalDimensionLengthMin}
                    onChange={handleLengthMinChange}
                    suffix={` ${suffix}`}
                    min={Constants.INTERNAL_DIMENSION_MIN}
                    max={Constants.INTERNAL_DIMENSION_MAX}
                />
                <NumberInputField
                    name="internalDimensionLengthMax"
                    label={lengthToLabel}
                    value={filters.internalDimensionLengthMax}
                    onChange={handleLengthMaxChange}
                    suffix={` ${suffix}`}
                    min={Constants.INTERNAL_DIMENSION_MIN}
                    max={Constants.INTERNAL_DIMENSION_MAX}
                />
            </ColumnFilterRow>
            <ColumnFilterRow fields={[{label: widthFromLabel, suffix}, {label: widthToLabel, suffix}]}>
                <NumberInputField
                    name="internalDimensionWidthMin"
                    label={widthFromLabel}
                    value={filters.internalDimensionWidthMin}
                    onChange={handleWidthMinChange}
                    suffix={` ${suffix}`}
                    min={Constants.INTERNAL_DIMENSION_MIN}
                    max={Constants.INTERNAL_DIMENSION_MAX}
                />
                <NumberInputField
                    name="internalDimensionWidthMax"
                    label={widthToLabel}
                    value={filters.internalDimensionWidthMax}
                    onChange={handleWidthMaxChange}
                    suffix={` ${suffix}`}
                    min={Constants.INTERNAL_DIMENSION_MIN}
                    max={Constants.INTERNAL_DIMENSION_MAX}
                />
            </ColumnFilterRow>
            <ColumnFilterRow fields={[{label: heightFromLabel, suffix}, {label: heightToLabel, suffix}]}>
                <NumberInputField
                    name="internalDimensionHeightMin"
                    label={heightFromLabel}
                    value={filters.internalDimensionHeightMin}
                    onChange={handleHeightMinChange}
                    suffix={` ${suffix}`}
                    min={Constants.INTERNAL_DIMENSION_MIN}
                    max={Constants.INTERNAL_DIMENSION_MAX}
                />
                <NumberInputField
                    name="internalDimensionHeightMax"
                    label={heightToLabel}
                    value={filters.internalDimensionHeightMax}
                    onChange={handleHeightMaxChange}
                    suffix={` ${suffix}`}
                    min={Constants.INTERNAL_DIMENSION_MIN}
                    max={Constants.INTERNAL_DIMENSION_MAX}
                />
            </ColumnFilterRow>
        </Box>
    );
};

/** The "Anfragemenge" column's filter content: "von" and "bis" side by side, suffixed "Stk.". */
const RequestQuantityColumnFilter: React.FC<RequestRangeColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const suffix = t(RequestI18N.details.field.pcsShort);
    const fromLabel = `${t(RequestI18N.list.column.quantity)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const toLabel = `${t(RequestI18N.list.column.quantity)} (${t(OverviewTableI18N.field.rangeTo)})`;

    const handleMinChange = useNumberFieldChange("quantityMin", onChange);
    const handleMaxChange = useNumberFieldChange("quantityMax", onChange);

    return (
        <ColumnFilterRow fields={[{label: fromLabel, suffix}, {label: toLabel, suffix}]}>
            <NumberInputField
                name="quantityMin"
                label={fromLabel}
                value={filters.quantityMin}
                onChange={handleMinChange}
                suffix={` ${suffix}`}
                min={Constants.QUANTITY_MIN}
                max={Constants.QUANTITY_MAX}
            />
            <NumberInputField
                name="quantityMax"
                label={toLabel}
                value={filters.quantityMax}
                onChange={handleMaxChange}
                suffix={` ${suffix}`}
                min={Constants.QUANTITY_MIN}
                max={Constants.QUANTITY_MAX}
            />
        </ColumnFilterRow>
    );
};

/** The "Netto für Hersteller" column's filter content: "von" and "bis" side by side, currency-formatted. */
const RequestNetPriceColumnFilter: React.FC<RequestRangeColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const suffix = getCurrencySymbol(getI18n().language);
    const fromLabel = `${t(RequestI18N.details.field.netPriceShort)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const toLabel = `${t(RequestI18N.details.field.netPriceShort)} (${t(OverviewTableI18N.field.rangeTo)})`;

    const handleMinChange = useNumberFieldChange("netPriceMin", onChange);
    const handleMaxChange = useNumberFieldChange("netPriceMax", onChange);

    return (
        <ColumnFilterRow fields={[{label: fromLabel, suffix}, {label: toLabel, suffix}]}>
            <CurrencyInputField
                name="netPriceMin"
                label={fromLabel}
                value={filters.netPriceMin}
                onChange={handleMinChange}
                min={0}
                max={Constants.REQUEST_SEARCH_NET_PRICE_MAX}
            />
            <CurrencyInputField
                name="netPriceMax"
                label={toLabel}
                value={filters.netPriceMax}
                onChange={handleMaxChange}
                min={0}
                max={Constants.REQUEST_SEARCH_NET_PRICE_MAX}
            />
        </ColumnFilterRow>
    );
};

/** The "Gesamtpreis" column's filter content: "von" and "bis" side by side, currency-formatted. */
const RequestTotalPriceColumnFilter: React.FC<RequestRangeColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const suffix = getCurrencySymbol(getI18n().language);
    const fromLabel = `${t(RequestI18N.details.field.totalPriceShort)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const toLabel = `${t(RequestI18N.details.field.totalPriceShort)} (${t(OverviewTableI18N.field.rangeTo)})`;

    const handleMinChange = useNumberFieldChange("totalPriceMin", onChange);
    const handleMaxChange = useNumberFieldChange("totalPriceMax", onChange);

    return (
        <ColumnFilterRow fields={[{label: fromLabel, suffix}, {label: toLabel, suffix}]}>
            <CurrencyInputField
                name="totalPriceMin"
                label={fromLabel}
                value={filters.totalPriceMin}
                onChange={handleMinChange}
                min={0}
                max={Constants.REQUEST_SEARCH_TOTAL_PRICE_MAX}
            />
            <CurrencyInputField
                name="totalPriceMax"
                label={toLabel}
                value={filters.totalPriceMax}
                onChange={handleMaxChange}
                min={0}
                max={Constants.REQUEST_SEARCH_TOTAL_PRICE_MAX}
            />
        </ColumnFilterRow>
    );
};

/** The "Gültig bis" column's filter content: "von" and "bis" date fields side by side, no unit suffix
 *  (Entscheidung 10 — date ranges stay two single date fields, never a combined range picker). */
const RequestValidUntilColumnFilter: React.FC<RequestRangeColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const fromLabel = `${t(RequestI18N.details.column.validUntil)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const toLabel = `${t(RequestI18N.details.column.validUntil)} (${t(OverviewTableI18N.field.rangeTo)})`;

    const handleFromChange = useDateFieldChange("validUntilFrom", onChange);
    const handleToChange = useDateFieldChange("validUntilTo", onChange);

    return (
        <ColumnFilterRow fields={[{label: fromLabel}, {label: toLabel}]}>
            <DateInputField
                name="validUntilFrom"
                label={fromLabel}
                value={filters.validUntilFrom ?? null}
                onChange={handleFromChange}
            />
            <DateInputField
                name="validUntilTo"
                label={toLabel}
                value={filters.validUntilTo ?? null}
                onChange={handleToChange}
            />
        </ColumnFilterRow>
    );
};

/** The "Lieferzeitraum" column's filter content: "von" and "bis" date fields side by side (Entscheidung 10). */
const RequestDeliveryDateColumnFilter: React.FC<RequestRangeColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const fromLabel = `${t(RequestI18N.details.field.deliveryDateRange)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const toLabel = `${t(RequestI18N.details.field.deliveryDateRange)} (${t(OverviewTableI18N.field.rangeTo)})`;

    const handleFromChange = useDateFieldChange("deliveryDateFrom", onChange);
    const handleToChange = useDateFieldChange("deliveryDateTo", onChange);

    return (
        <ColumnFilterRow fields={[{label: fromLabel}, {label: toLabel}]}>
            <DateInputField
                name="deliveryDateFrom"
                label={fromLabel}
                value={filters.deliveryDateFrom ?? null}
                onChange={handleFromChange}
            />
            <DateInputField
                name="deliveryDateTo"
                label={toLabel}
                value={filters.deliveryDateTo ?? null}
                onChange={handleToChange}
            />
        </ColumnFilterRow>
    );
};

/** The "Entfernung" column's filter content — Angebotsgebersicht only (Entscheidung 6, `type !==
 *  RequestFilter.EXTERNAL` renders nothing, see `renderRequestRangeColumnFilter` below): "von" and "bis"
 *  side by side, suffixed "km". */
const RequestDistanceColumnFilter: React.FC<RequestRangeColumnFilterProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const suffix = KILOMETRE_UNIT;
    const fromLabel = `${t(RequestI18N.details.field.distanceToRequester)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const toLabel = `${t(RequestI18N.details.field.distanceToRequester)} (${t(OverviewTableI18N.field.rangeTo)})`;

    const handleMinChange = useNumberFieldChange("distanceKmMin", onChange);
    const handleMaxChange = useNumberFieldChange("distanceKmMax", onChange);

    return (
        <ColumnFilterRow fields={[{label: fromLabel, suffix}, {label: toLabel, suffix}]}>
            <NumberInputField
                name="distanceKmMin"
                label={fromLabel}
                value={filters.distanceKmMin}
                onChange={handleMinChange}
                suffix={` ${suffix}`}
                min={0}
                max={Constants.REQUEST_SEARCH_DISTANCE_KM_MAX}
            />
            <NumberInputField
                name="distanceKmMax"
                label={toLabel}
                value={filters.distanceKmMax}
                onChange={handleMaxChange}
                suffix={` ${suffix}`}
                min={0}
                max={Constants.REQUEST_SEARCH_DISTANCE_KM_MAX}
            />
        </ColumnFilterRow>
    );
};

/**
 * Renders one request range column's filter content for `DataTable`'s `renderColumnFilter` (plan
 * "Spaltenkopf-Filterung und Sortieranzeige", Phase 3.2) — looked up by `columnId` (`meta.filterColumnId`),
 * called from `renderRequestColumnFilter` in `RequestColumnFilters.tsx` alongside the select/text columns
 * wired in Phase 3.1. `type` gates the Angebotsgebersicht-only distance column, mirroring the check
 * `RequestOverviewTable.tsx` already applies before setting `meta.filterColumnId` on that column at all.
 */
export function renderRequestRangeColumnFilter(
    columnId: string,
    type: RequestFilter,
    filters: RequestOverviewFilters,
    onChange: (partial: Partial<RequestOverviewFilters>) => void
): React.ReactNode | undefined {
    switch (columnId) {
        case REQUEST_OVERVIEW_COLUMN_IDS.internalDimensions:
            return <RequestInternalDimensionsColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions1:
            return <RequestQuantityColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions2:
            return <RequestNetPriceColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions3:
            return <RequestTotalPriceColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.validUntil:
            return <RequestValidUntilColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.deliveryDateRange:
            return <RequestDeliveryDateColumnFilter filters={filters} onChange={onChange} />;
        case REQUEST_OVERVIEW_COLUMN_IDS.distanceMeters:
            return type === RequestFilter.EXTERNAL ? (
                <RequestDistanceColumnFilter filters={filters} onChange={onChange} />
            ) : undefined;
        default:
            return undefined;
    }
}
